import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import { formMappings } from "../../../config/formMappings";

// Approximate center coordinates for US states (longitude, latitude)
const STATE_CENTERS: { [key: string]: [number, number] } = {
  Alabama: [-86.7911, 32.8067],
  Alaska: [-152.4044, 61.3707],
  Arizona: [-111.4312, 33.7298],
  Arkansas: [-92.3731, 34.9697],
  California: [-119.4179, 36.1162],
  Colorado: [-105.3111, 39.0598],
  Connecticut: [-72.7554, 41.5978],
  Delaware: [-75.5277, 39.3185],
  "District of Columbia": [-77.0268, 38.9072],
  Florida: [-81.5158, 27.7663],
  Georgia: [-83.1132, 33.0406],
  Hawaii: [-157.4983, 21.0943],
  Idaho: [-114.4788, 44.2405],
  Illinois: [-89.3985, 40.3495],
  Indiana: [-86.1477, 39.8494],
  Iowa: [-93.2105, 42.0115],
  Kansas: [-98.4842, 38.5266],
  Kentucky: [-84.6701, 37.6681],
  Louisiana: [-92.3291, 31.1695],
  Maine: [-69.3977, 44.3235],
  Maryland: [-76.5019, 39.0639],
  Massachusetts: [-71.5301, 42.2302],
  Michigan: [-84.5467, 43.3266],
  Minnesota: [-94.6859, 45.6945],
  Mississippi: [-89.6678, 32.7416],
  Missouri: [-92.1893, 38.4561],
  Montana: [-110.4544, 47.0527],
  Nebraska: [-98.2681, 41.1254],
  Nevada: [-117.0554, 38.3135],
  "New Hampshire": [-71.5653, 43.4525],
  "New Jersey": [-74.521, 40.2989],
  "New Mexico": [-106.2485, 34.8405],
  "New York": [-74.9481, 42.1657],
  "North Carolina": [-79.3877, 35.6301],
  "North Dakota": [-99.784, 47.5289],
  Ohio: [-82.7649, 40.3888],
  Oklahoma: [-97.5349, 35.5653],
  Oregon: [-122.0709, 44.572],
  Pennsylvania: [-77.2098, 40.5908],
  "Rhode Island": [-71.5118, 41.6809],
  "South Carolina": [-80.945, 33.8569],
  "South Dakota": [-99.9018, 44.2998],
  Tennessee: [-86.7844, 35.7478],
  Texas: [-99.9018, 31.0545],
  Utah: [-111.8926, 40.15],
  Vermont: [-72.7317, 44.0459],
  Virginia: [-78.1694, 37.7693],
  Washington: [-121.4905, 47.4009],
  "West Virginia": [-80.9696, 38.4912],
  Wisconsin: [-89.6165, 44.2685],
  Wyoming: [-107.3025, 42.7559],
  // Canadian provinces
  Alberta: [-114.0719, 53.9333],
  "British Columbia": [-125.0, 53.7267],
  Manitoba: [-98.8139, 53.7609],
  "New Brunswick": [-66.4619, 46.5653],
  "Newfoundland and Labrador": [-56.0, 53.0],
  "Nova Scotia": [-63.0, 45.0],
  "Northwest Territories": [-117.0, 64.0],
  Nunavut: [-85.0, 70.0],
  Ontario: [-85.0, 50.0],
  "Prince Edward Island": [-63.0, 46.25],
  Quebec: [-71.0, 52.0],
  Saskatchewan: [-106.0, 54.0],
  Yukon: [-135.0, 64.0],
  Canada: [-95.0, 60.0],
};

// State name mapping: abbreviation to full name
const STATE_ABBREVIATIONS: { [key: string]: string } = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  DC: "District of Columbia",
  // Canadian provinces and territories
  AB: "Alberta",
  BC: "British Columbia",
  MB: "Manitoba",
  NB: "New Brunswick",
  NL: "Newfoundland and Labrador",
  NS: "Nova Scotia",
  NT: "Northwest Territories",
  NU: "Nunavut",
  ON: "Ontario",
  PE: "Prince Edward Island",
  QC: "Quebec",
  SK: "Saskatchewan",
  YT: "Yukon",
};

// Reverse mapping: full name to abbreviation
const STATE_FULL_NAMES: { [key: string]: string } = Object.fromEntries(
  Object.entries(STATE_ABBREVIATIONS).map(([abbr, full]) => [full, abbr])
);

/**
 * Normalizes state names to a consistent format (prefers full name)
 * Handles both abbreviations (CA) and full names (California)
 */
function normalizeStateName(state: string): string {
  if (!state || typeof state !== "string") return state;

  const trimmed = state.trim();

  // If it's a 2-letter abbreviation, convert to full name
  if (trimmed.length === 2 && STATE_ABBREVIATIONS[trimmed.toUpperCase()]) {
    return STATE_ABBREVIATIONS[trimmed.toUpperCase()];
  }

  // If it's a full name, check if we have it in our mapping
  const upperKey = Object.keys(STATE_FULL_NAMES).find(
    (key) => key.toLowerCase() === trimmed.toLowerCase()
  );
  if (upperKey) {
    return upperKey; // Return the properly capitalized version
  }

  // If not found in mapping, return as-is (could be a province or other location)
  return trimmed;
}

/**
 * Get color based on count (darker = more registrants)
 */
function getColorForCount(count: number, maxCount: number): string {
  if (count === 0) return "#f9fafb"; // Light gray for no data
  const intensity = Math.min(count / maxCount, 1);
  // Use a more pronounced color scale from light to dark
  // Map intensity to a color scale: light blue -> medium blue -> dark blue
  if (intensity < 0.2) {
    return "#dbeafe"; // Very light blue
  } else if (intensity < 0.4) {
    return "#93c5fd"; // Light blue
  } else if (intensity < 0.6) {
    return "#60a5fa"; // Medium blue
  } else if (intensity < 0.8) {
    return "#3b82f6"; // Blue
  } else {
    return "#1b3356"; // Dark blue (primary color)
  }
}

export default function LocationDistributionMap() {
  const entries = useSelector((state: RootState) => state.formEntries.entries);

  const activeFormName = useSelector(
    (state: RootState) => state.formEntries.activeFormName || ""
  );

  const formMapping = formMappings[activeFormName as keyof typeof formMappings];
  const version = formMapping?.version || "1";
  const addressObjectKey = (formMapping?.fieldMappings as any)?.address_object;
  const stateFieldKey = (formMapping?.fieldMappings as any)?.state;
  const numberOfRegistrantsFieldKey = (formMapping?.fieldMappings as any)?.[
    "Number of Registrants"
  ];

  // Calculate location distribution (same logic as LocationDistributionTable)
  const locationDistribution = entries.reduce((acc, entry) => {
    let state: string | null = null;

    if (version === "1") {
      // Version 1: Get state from address_object
      if (addressObjectKey && entry.meta[addressObjectKey]) {
        const addressObject = entry.meta[addressObjectKey] as any;
        if (
          addressObject &&
          typeof addressObject === "object" &&
          addressObject.state
        ) {
          state = addressObject.state;
        }
      }
    } else {
      // Version 2+: Get state directly from mapped field
      if (stateFieldKey && entry.meta[stateFieldKey]) {
        const stateValue = entry.meta[stateFieldKey];
        if (Array.isArray(stateValue)) {
          state = stateValue[0];
        } else if (typeof stateValue === "string") {
          state = stateValue;
        } else {
          state = String(stateValue);
        }
      }
    }

    // Get the number of registrants for this entry
    const numberOfRegistrants = parseInt(
      entry.meta[numberOfRegistrantsFieldKey || ""] ||
        entry.meta["Number of Registrants"] ||
        "1",
      10
    );
    const count = isNaN(numberOfRegistrants) ? 1 : numberOfRegistrants;

    // Normalize and count the state by number of registrants
    if (state) {
      const normalizedState = normalizeStateName(state);
      if (normalizedState) {
        acc[normalizedState] = (acc[normalizedState] || 0) + count;
      }
    }

    return acc;
  }, {} as { [location: string]: number });

  // Find max count for color scaling
  const maxCount = Math.max(...Object.values(locationDistribution), 1);

  const usGeoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";
  const canadaGeoUrl =
    "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

  return (
    <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6 col-span-2">
      <h1 className="text-xl text-primary text-center">
        Location Distribution
      </h1>
      <ComposableMap projection="geoAlbersUsa">
        {/* US States */}
        <Geographies geography={usGeoUrl}>
          {({ geographies }) => {
            return (
              <>
                {geographies.map((geo) => {
                  // Try multiple possible property names - US atlas uses different property names
                  const props = geo.properties || {};
                  const stateName =
                    props.NAME ||
                    props.name ||
                    props.NAME_1 ||
                    props.NAME_LONG ||
                    props.name_long ||
                    "";

                  if (!stateName) {
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill="#f9fafb"
                        stroke="#111827"
                        strokeWidth={0.5}
                      />
                    );
                  }

                  // Find matching count - try exact match first, then case-insensitive
                  let count = locationDistribution[stateName] || 0;
                  if (count === 0 && stateName) {
                    // Try case-insensitive match
                    const matchingKey = Object.keys(locationDistribution).find(
                      (key) =>
                        key &&
                        stateName &&
                        key.toLowerCase() === stateName.toLowerCase()
                    );
                    if (matchingKey) {
                      count = locationDistribution[matchingKey];
                    }
                  }

                  const fillColor = getColorForCount(count, maxCount);

                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={fillColor}
                      stroke="#111827"
                      strokeWidth={0.5}
                    />
                  );
                })}
                {/* Add text labels for states with registrants */}
                {geographies.map((geo) => {
                  // Try multiple possible property names
                  const props = geo.properties || {};
                  const stateName =
                    props.NAME ||
                    props.name ||
                    props.NAME_1 ||
                    props.NAME_LONG ||
                    props.name_long ||
                    "";

                  if (!stateName) return null;

                  // Find matching count - try exact match first, then case-insensitive
                  let count = locationDistribution[stateName] || 0;
                  let matchedStateName = stateName;
                  if (count === 0) {
                    // Try case-insensitive match
                    const matchingKey = Object.keys(locationDistribution).find(
                      (key) =>
                        key &&
                        stateName &&
                        key.toLowerCase() === stateName.toLowerCase()
                    );
                    if (matchingKey) {
                      count = locationDistribution[matchingKey];
                      matchedStateName = matchingKey;
                    }
                  }

                  if (count === 0) return null;

                  // Get center coordinates from predefined mapping
                  const center =
                    STATE_CENTERS[matchedStateName] || STATE_CENTERS[stateName];
                  if (!center) {
                    return null;
                  }

                  return (
                    <Marker key={`label-${geo.rsmKey}`} coordinates={center}>
                      <text
                        x={0}
                        y={0}
                        textAnchor="middle"
                        style={{
                          fontFamily: "system-ui, sans-serif",
                          fontSize: "14px",
                          fontWeight: "bold",
                          fill: count > maxCount * 0.5 ? "#ffffff" : "#111827",
                          pointerEvents: "none",
                          textShadow:
                            count > maxCount * 0.5
                              ? "1px 1px 2px rgba(0,0,0,0.5)"
                              : "1px 1px 2px rgba(255,255,255,0.8)",
                        }}
                      >
                        {count}
                      </text>
                    </Marker>
                  );
                })}
              </>
            );
          }}
        </Geographies>
        {/* Canada */}
        <Geographies geography={canadaGeoUrl}>
          {({ geographies }) => (
            <>
              {geographies
                .filter((geo) => geo.properties.NAME === "Canada")
                .map((geo) => {
                  const count = locationDistribution["Canada"] || 0;
                  const fillColor = getColorForCount(count, maxCount);
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={fillColor}
                      stroke="#111827"
                      strokeWidth={0.5}
                    />
                  );
                })}
              {/* Add label for Canada if it has registrants */}
              {geographies
                .filter((geo) => geo.properties.NAME === "Canada")
                .map((geo) => {
                  const count = locationDistribution["Canada"] || 0;
                  if (count === 0) return null;

                  // Use predefined center of Canada for label
                  const center = STATE_CENTERS["Canada"];

                  return (
                    <Marker key={`label-canada`} coordinates={center}>
                      <text
                        x={0}
                        y={0}
                        textAnchor="middle"
                        style={{
                          fontFamily: "system-ui, sans-serif",
                          fontSize: "12px",
                          fontWeight: "bold",
                          fill: count > maxCount * 0.5 ? "#ffffff" : "#111827",
                          pointerEvents: "none",
                        }}
                      >
                        {count}
                      </text>
                    </Marker>
                  );
                })}
            </>
          )}
        </Geographies>
      </ComposableMap>
    </div>
  );
}
